import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/app/Layout";
import WineCard from "../components/app/WineCard";
import RequestQuoteModal from "../components/app/RequestQuoteModal";
import BuyRequestModal from "../components/app/BuyRequestModal";
import MockPaymentModal from "../components/app/MockPaymentModal";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function WineryProfile() {
    const { slug } = useParams();
    const [winery, setWinery] = useState(null);
    const [wines, setWines] = useState([]);
    const [tab, setTab] = useState("wines");
    const [quoteWine, setQuoteWine] = useState(null);
    const [quoteMode, setQuoteMode] = useState("quote");
    const [buyWine, setBuyWine] = useState(null);
    const [paynowWine, setPaynowWine] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        api.get(`/wineries/${slug}`).then(({ data }) => setWinery(data)).catch(() => setNotFound(true));
        api.get(`/wines?winery_slug=${slug}`).then(({ data }) => setWines(data)).catch(() => {});
    }, [slug]);

    const onQuote = (wine, mode = "quote") => {
        if (!user) {
            toast.info("Please log in to request a quote.");
            navigate(`/login?redirect=/winery/${slug}`);
            return;
        }
        if (mode === "buy") {
            setBuyWine(wine);
            return;
        }
        if (mode === "paynow") {
            setPaynowWine(wine);
            return;
        }
        setQuoteMode(mode);
        setQuoteWine(wine);
    };

    if (notFound) {
        return <Layout><div className="px-6 py-32 text-center"><p className="font-display text-4xl">Winery not found.</p></div></Layout>;
    }
    if (!winery) return <Layout><div className="px-6 py-32 text-[#71717A]">Loading...</div></Layout>;

    return (
        <Layout transparentNav>
            {/* Banner */}
            <section className="relative h-[70vh] w-full overflow-hidden" data-testid="winery-banner">
                <img
                    src={winery.banner_url || "https://images.unsplash.com/photo-1629424511021-1be28bf5ae1e?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"}
                    alt={winery.name}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/55" />
                <div className="absolute inset-0 flex items-end px-6 md:px-12 lg:px-24 pb-16">
                    <div>
                        {winery.logo_url && (
                            <img src={winery.logo_url} alt={`${winery.name} logo`} className="w-20 h-20 object-contain bg-black/40 border border-[#C9A961] p-3 mb-6" />
                        )}
                        <p className="overline">{winery.region}{winery.status === "premium" && " · Premium"}</p>
                        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl mt-4 leading-none">{winery.name}</h1>
                        {winery.established_year && <p className="text-base text-[#E4E4E7] mt-4 tracking-wide">Established {winery.established_year}</p>}
                        {wines.length > 0 && (
                            <button
                                onClick={() => onQuote(wines[0])}
                                className="btn-gold !px-10 !py-5 !text-base mt-10"
                                data-testid="winery-hero-quote"
                            >
                                Request Quote
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* Tabs */}
            <section className="px-6 md:px-12 lg:px-16 border-b border-[#27272A] bg-black" data-testid="winery-tabs">
                <div className="flex gap-8">
                    {[
                        { id: "story", label: "Story" },
                        { id: "wines", label: `Wines (${wines.length})` },
                        ...(winery.gallery_images?.length ? [{ id: "gallery", label: "Gallery" }] : []),
                        { id: "contact", label: "Contact" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`tab-${t.id}`}
                            className={`py-6 text-sm tracking-[0.15em] uppercase transition-colors relative ${tab === t.id ? "text-[#C9A961]" : "text-[#71717A] hover:text-white"}`}
                        >
                            {t.label}
                            {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#C9A961]" />}
                        </button>
                    ))}
                </div>
            </section>

            {/* Content */}
            <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="winery-content">
                {tab === "story" && (
                    <div className="max-w-3xl">
                        <p className="overline">The Story</p>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 mb-10">A House of Terroir</h2>
                        <div className="text-lg text-[#E4E4E7] leading-relaxed whitespace-pre-wrap">
                            {winery.story || "This winery has not yet shared their story."}
                        </div>
                    </div>
                )}
                {tab === "wines" && (
                    wines.length === 0 ? (
                        <div className="border border-[#27272A] p-16 text-center"><p className="font-display text-3xl text-[#A1A1AA]">No wines listed yet.</p></div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {wines.map((w) => <WineCard key={w.id} wine={w} onQuote={onQuote} />)}
                        </div>
                    )
                )}
                {tab === "gallery" && winery.gallery_images?.length > 0 && (
                    <div data-testid="winery-gallery">
                        <p className="overline">Gallery</p>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 mb-12">The Estate</h2>
                        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                            {winery.gallery_images.map((src, i) => (
                                <div key={i} className="aspect-[4/3] overflow-hidden bg-[#0A0A0A]">
                                    <img src={src} alt={`${winery.name} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {tab === "contact" && (
                    <div className="max-w-xl">
                        <p className="overline">Contact</p>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 mb-6">Get in Touch</h2>
                        <p className="text-base text-[#A1A1AA] mb-10">Contact us for export inquiries and quotes.</p>
                        <div className="space-y-6 text-[#E4E4E7]">
                            <div>
                                <p className="text-xs text-[#71717A] uppercase tracking-wide">Email</p>
                                <p className="text-lg mt-1">{winery.email}</p>
                            </div>
                            <div>
                                <p className="text-xs text-[#71717A] uppercase tracking-wide">Region</p>
                                <p className="text-lg mt-1">{winery.region}</p>
                            </div>
                            {wines.length > 0 && (
                                <button onClick={() => onQuote(wines[0])} className="btn-gold mt-4" data-testid="contact-get-in-touch">
                                    Get in Touch
                                </button>
                            )}
                            <p className="text-sm text-[#A1A1AA] mt-12 pt-8 border-t border-[#27272A]">
                                To order or request a quote, click "Request Quote" on any wine. Our verified channel ensures direct communication with the producer.
                            </p>
                        </div>
                    </div>
                )}
            </section>

            {quoteWine && <RequestQuoteModal wine={quoteWine} mode={quoteMode} onClose={() => setQuoteWine(null)} />}
            {buyWine && <BuyRequestModal wine={buyWine} onClose={() => setBuyWine(null)} />}
            {paynowWine && (
                <MockPaymentModal
                    title={`Pagar — ${paynowWine.name}`}
                    subtitle={`${paynowWine.winery_name} · ${paynowWine.vintage}`}
                    amount={Math.round(paynowWine.price_fob_usd)}
                    currency="USD"
                    onClose={() => setPaynowWine(null)}
                    testId="wine-mock-pay"
                />
            )}
        </Layout>
    );
}
